/**
 * Content Manager Service
 *
 * Orchestrates fetching content from all platforms
 * and classifying it into sections
 */

import youtubeService from './youtube';
import instagramService from './instagram';
import tiktokService from './tiktok';
import contentClassifier from './contentClassifier';

class ContentManager {
  constructor() {
    this.allContent = [];
    this.classifiedContent = null;
    this.loading = false;
    this.errors = [];
  }

  /**
   * Fetch and classify all content from all platforms
   */
  async fetchAllContent() {
    this.loading = true;
    this.errors = [];
    const allContent = [];

    try {
      // Fetch YouTube content
      const youtubeVideos = await this.fetchYouTubeContent();
      allContent.push(...youtubeVideos);
    } catch (error) {
      console.error('YouTube fetch failed:', error);
      this.errors.push({ platform: 'youtube', error: error.message });
    }

    try {
      // Fetch Instagram content
      const instagramPosts = await this.fetchInstagramContent();
      allContent.push(...instagramPosts);
    } catch (error) {
      console.error('Instagram fetch failed:', error);
      this.errors.push({ platform: 'instagram', error: error.message });
    }

    try {
      // Fetch TikTok content
      const tiktokVideos = await this.fetchTikTokContent();
      allContent.push(...tiktokVideos);
    } catch (error) {
      console.error('TikTok fetch failed:', error);
      this.errors.push({ platform: 'tiktok', error: error.message });
    }

    // Store all content
    this.allContent = allContent;

    // Classify content into sections
    this.classifiedContent = contentClassifier.classifyContent(allContent);

    this.loading = false;

    return {
      allContent: this.allContent,
      classified: this.classifiedContent,
      errors: this.errors,
    };
  }

  /**
   * Fetch YouTube content
   */
  async fetchYouTubeContent() {
    try {
      const videos = await youtubeService.getChannelVideos();
      return videos;
    } catch (error) {
      console.error('YouTube fetch error:', error);
      return [];
    }
  }

  /**
   * Fetch Instagram content
   */
  async fetchInstagramContent() {
    try {
      const posts = await instagramService.getUserMedia();
      return posts;
    } catch (error) {
      console.error('Instagram fetch error:', error);
      return [];
    }
  }

  /**
   * Fetch TikTok content
   */
  async fetchTikTokContent() {
    try {
      const videos = await tiktokService.getVideos();
      return videos;
    } catch (error) {
      console.error('TikTok fetch error:', error);
      return [];
    }
  }

  /**
   * Get content for a specific section
   */
  getSectionContent(section) {
    if (!this.classifiedContent) {
      return [];
    }
    return this.classifiedContent[section] || [];
  }

  /**
   * Get hero content (usually YouTube study videos)
   */
  getHeroContent() {
    // Prioritize YouTube videos for hero section
    // Filter out YouTube Shorts (videos <= 60 seconds)
    const youtubeContent = this.allContent.filter(item =>
      item.platform === 'youtube' &&
      (!item.durationSeconds || item.durationSeconds > 120)
    );

    if (youtubeContent.length > 0) {
      // Return the most recent horizontal video
      return youtubeContent.sort((a, b) => {
        const dateA = new Date(a.publishedAt);
        const dateB = new Date(b.publishedAt);
        return dateB - dateA;
      })[0];
    }

    // Fallback to any YouTube content (including Shorts if no horizontal videos)
    const anyYouTubeContent = this.allContent.filter(item => item.platform === 'youtube');
    if (anyYouTubeContent.length > 0) {
      return anyYouTubeContent.sort((a, b) => {
        const dateA = new Date(a.publishedAt);
        const dateB = new Date(b.publishedAt);
        return dateB - dateA;
      })[0];
    }

    // Final fallback to any content
    return this.allContent[0] || null;
  }

  /**
   * Refresh content
   */
  async refresh() {
    // Clear caches
    youtubeService.clearCache();
    instagramService.clearCache();
    tiktokService.clearCache();

    // Fetch fresh content
    return await this.fetchAllContent();
  }

  /**
   * Get loading state
   */
  isLoading() {
    return this.loading;
  }

  /**
   * Get errors
   */
  getErrors() {
    return this.errors;
  }
}

export default new ContentManager();
